import React, { useState, useMemo } from 'react';
import { useAdminContext } from '../../../context/AdminContext';
import { Download } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';

const AgreementExportReport = () => {
    const { allAgreements, loading } = useAdminContext();

    // Estados para los filtros
    const [filterStatus, setFilterStatus] = useState('todos');
    const [filterAccelerator, setFilterAccelerator] = useState('todos');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');

    const filteredAgreements = useMemo(() => {
        if (!allAgreements) return [];
        return allAgreements.filter(a => {
            const statusMatch = filterStatus === 'todos' || a.status === filterStatus;
            const acceleratorMatch = filterAccelerator === 'todos' || (filterAccelerator === 'con' && a.acceleratorActive) || (filterAccelerator === 'sin' && !a.acceleratorActive);
            
            const agreementDate = a.createdAt?.toDate();
            const startDateMatch = !filterStartDate || (agreementDate && agreementDate >= new Date(filterStartDate));
            const endDateMatch = !filterEndDate || (agreementDate && agreementDate <= new Date(filterEndDate + 'T23:59:59'));

            return statusMatch && acceleratorMatch && startDateMatch && endDateMatch;
        });
    }, [allAgreements, filterStatus, filterAccelerator, filterStartDate, filterEndDate]);

    const handleExport = (format) => {
        if (filteredAgreements.length === 0) {
            Swal.fire('Atención', 'No hay datos que coincidan con los filtros para exportar.', 'info');
            return;
        }

        const headers = ['ID Acuerdo', 'Email Usuario', 'Monto aportado ($)', 'Monto neto ($)', 'Valor Actual ($)', 'Estado', 'Acelerador'];
        const rows = filteredAgreements.map(agreement => [
            agreement.id,
            agreement.userEmail,
            agreement.amount.toFixed(2),
            (agreement.netInvestmentAmount || agreement.amount).toFixed(2),
            (agreement.currentValue || (agreement.netInvestmentAmount || agreement.amount)).toFixed(2),
            agreement.status,
            agreement.acceleratorActive ? 'Sí' : 'No'
        ]);

        const date = new Date().toISOString().split('T')[0];

        if (format === 'csv') {
            let csvContent = "data:text/csv;charset=utf-8," 
                + headers.join(",") + "\n" 
                + rows.map(e => e.join(",")).join("\n");

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `reporte_acuerdos_${date}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else if (format === 'pdf') {
            const doc = new jsPDF({ orientation: 'landscape' });
            doc.text(`Reporte de Acuerdos - ${date}`, 14, 16);
            autoTable(doc, {
                head: [headers],
                body: rows,
                startY: 20,
                theme: 'grid',
                styles: { fontSize: 8, cellPadding: 2 },
                headStyles: { fillColor: [44, 94, 78] }
            });
            doc.save(`reporte_acuerdos_${date}.pdf`);
        }
    };

    return (
        <div className="report-container">
            <h4>Exportar Acuerdos</h4>
            <p>Usa los filtros para seleccionar el conjunto de datos que deseas exportar y luego elige el formato.</p>

            <div className="filters-container" style={{ marginTop: '1.5rem', padding: '1.5rem' }}>
                <div className="input-group">
                    <label>Estado del Acuerdo</label>
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                        <option value="todos">Todos</option>
                        <option value="aprobado">Aprobado</option>
                        <option value="pendiente">Pendiente</option>
                        <option value="rechazado">Rechazado</option>
                        <option value="completado">Completado</option>
                        <option value="cancelado">Cancelado</option>
                    </select>
                </div>
                <div className="input-group">
                    <label>Acelerador</label>
                    <select value={filterAccelerator} onChange={e => setFilterAccelerator(e.target.value)}>
                        <option value="todos">Todos</option>
                        <option value="con">Con Acelerador</option>
                        <option value="sin">Sin Acelerador</option>
                    </select>
                </div>
                <div className="input-group">
                    <label>Fecha de Inicio</label>
                    <input type="date" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} />
                </div>
                <div className="input-group">
                    <label>Fecha de Fin</label>
                    <input type="date" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} />
                </div>
            </div>

            <div className="export-summary">
                <p><strong>{filteredAgreements.length}</strong> acuerdos coinciden con los filtros.</p>
                <div className="export-buttons">
                    <button 
                        className="export-btn csv" 
                        onClick={() => handleExport('csv')}
                        disabled={loading || filteredAgreements.length === 0}
                    >
                        <Download size={16} /> Exportar a CSV
                    </button>
                    <button 
                        className="export-btn pdf" 
                        onClick={() => handleExport('pdf')}
                        disabled={loading || filteredAgreements.length === 0}
                    >
                        <Download size={16} /> Exportar a PDF
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AgreementExportReport;